@file:Suppress("ClassName")

package common.dto

import editor.EditorManager
import ext.deepClone
import ext.plus
import rendering.ModelManager
import three.js.Box3
import three.js.Group
import three.js.Object3D
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.sign

@Suppress("EnumEntryName")
enum class ElementType(
    val modelFile: String, val heightMultiplier: Double = 1.0, val lengthMultiplier: Double = 1.0,
    val longerName: String? = null
) {
    Wheels("wheels") {
        override fun heightFor(direction: Direction) = when (direction) {
            // a bit weird; we need FORWARD and BACKWARD because
            // for placement OF the wheels BACKWARD is used and for placement
            // ON the wheels FORWARD is used
            Direction.FORWARD, Direction.BACKWARD -> (32.0 - 8.0) * 2
            Direction.RIGHT, Direction.LEFT -> 24.0
            else -> 100.0
        }

        override fun allowsPlacement(direction: Direction, toBePlaced: ElementType) = when (toBePlaced) {
            // disallow placement on thin side of T profile
            // (not enough depth for brixi spheres to clamp)
            // also disallow up/down (direction of the actual wheels)
            Brixi -> direction in setOf(Direction.BACKWARD, Direction.RIGHT, Direction.LEFT)
            else -> false
        }

    },
    Brixi("brixi") {
        override fun heightFor(direction: Direction) = 16.0
        override fun allowsPlacement(direction: Direction, toBePlaced: ElementType) = when (toBePlaced) {
            Brixi -> false
            Twister -> true
            // bricks
            else -> true
        }

        /**
         * The Twister is only allowed to be placed *directly* on top of a brixi. Bricks may be offset
         * in x or y, however not in both directions combined.
         */
        override fun modifyOffset(toBePlaced: ElementType, offset: Vec2d) = when (toBePlaced) {
            Twister -> Vec2d(0.0, 0.0)
            // bricks
            else -> if (abs(offset.x) > abs(offset.y)) Vec2d(offset.x, 0.0) else Vec2d(0.0, offset.y)
        }

        override fun getRotationAround(
            toBePlaced: ElementType,
            scrollRotation: Int,
            // todo we shouldn't need to know these two values
            directionForPlaced: Direction,
            offset: Vec3d,
        ) = when (toBePlaced) {
            Twister, Brixi -> 0.0.aroundZ()
            // bricks
            // - if scroll rotation negative, rotate around line in negative y direction
            // - if scroll rotation positive, rotate around line in positive y direction
            // 4.0 all relative to without-offset-location of new elt !
            // todo shouldn't have to depend on toBePlaced height!
            else -> {
                val lineOrigin = Vec3d(0.0, -scrollRotation.sign * 9.0, -toBePlaced.heightFor(Direction.FORWARD) / 2)
                val lineDirection = Vec3d(x = 1.0)
                val line = Line3d(lineOrigin, lineDirection)

                val angle = ((scrollRotation * PI) / 32).coerceIn(-PI / 2, PI / 2)
                angle around line
            }
        }
    },
    Twister("twister") {
        override fun allowsPlacement(direction: Direction, toBePlaced: ElementType) = when (toBePlaced) {
            // disallow twister on twister placement for now.
            // todo: maybe some kind of "lax mode" where stuff like this is allowed?
            Brixi -> direction == Direction.FORWARD || direction == Direction.BACKWARD
            else -> false
        }

        override fun modifyOffset(toBePlaced: ElementType, offset: Vec2d) = Vec2d(0.0, 0.0)

        override fun getRotationAround(
            toBePlaced: ElementType,
            scrollRotation: Int,
            directionForPlaced: Direction,
            offset: Vec3d,
        ): RotationAround = when (toBePlaced) {
            Brixi -> (scrollRotation * PI / 16).aroundZ()
            else -> 0.0.aroundZ()
        }
    },

    `1x2`("brick", 1.0, 2.0, longerName = "No. 1 - 64"),
    `1x3`("brick", 1.0, 3.0, longerName = "No. 1 - 96"),
    `1x4`("brick", 1.0, 4.0, longerName = "No. 1 - 128"),
    `1x5`("brick", 1.0, 5.0, longerName = "No. 1 - 160"),

    `2x2`("brick", 2.0, 2.0, longerName = "No. 2 - 64"),
    `2x3`("brick", 2.0, 3.0, longerName = "No. 2 - 96"),
    `2x4`("brick", 2.0, 4.0, longerName = "No. 2 - 128"),
    `2x5`("brick", 2.0, 5.0, longerName = "No. 2 - 160"),

    Kapla("brick", 3.0, 3.5, longerName = "KAPLA"),

    `4x3`("brick", 4.0, 3.0, longerName = "No. 4 - 96");

    private lateinit var model: Object3D

    /**
     * Gets called by the [ModelManager] to load the model. Potentially applies
     * the scaling to the model.
     *
     * The positive z-axis of the model should be the "placeable" side, and the positive x-axis the
     * "secondary placeable side" (for the bricks). The model must already be oriented to support this,
     * but we must also apply the correct scaling to the model, so that this works.
     */
    fun loadModel(model: Object3D) {
        this.model = model.apply {
            scale.set(
                x = 1,
                y = lengthMultiplier,
                z = heightMultiplier
            )
            name = "InnerModel[$name]"
        }
    }

    fun cloneModel() = Group().apply {
        add(model.deepClone())
        name = "Model[$name]"
    }

    /**
     * Checks if this element type allows placement of the [toBePlaced] element in the given direction.
     */
    // for bricks:
    open fun allowsPlacement(direction: Direction, toBePlaced: ElementType) = when (toBePlaced) {
        Brixi -> direction == Direction.FORWARD ||
                direction == Direction.BACKWARD ||
                direction == Direction.UP ||
                direction == Direction.DOWN ||
                (direction == Direction.LEFT && heightMultiplier == 1.0) ||
                (direction == Direction.RIGHT && heightMultiplier == 1.0)

        Twister -> false
        // disallow placing bricks on another brick for now. todo: maybe some kind of "lax mode" where stuff like this is allowed?
        else -> false
    }

    /**
     * The diameter of the element in the given direction.
     *
     * For Brixis, this excludes the spheres because they do not matter for placement.
     */
    open fun heightFor(direction: Direction): Double {
        // todo: optimize
        val model = cloneModel()
        val box = Box3().setFromObject(model)

        return when (direction) {
            Direction.LEFT, Direction.RIGHT -> box.max.x.toDouble() - box.min.x.toDouble()
            Direction.UP, Direction.DOWN -> box.max.y.toDouble() - box.min.y.toDouble()
            Direction.FORWARD, Direction.BACKWARD -> box.max.z.toDouble() - box.min.z.toDouble()
        }
    }

    /**
     * Modify the offset for the element type, depending on [toBePlaced]. We are the
     * "target" element here, and toBePlaced is the element that is placed on top of us.
     *
     * By default, for bricks, we only allow offset along the y-axis (the "long side" of the brick), so
     * we remove the x component. On the other hand, when placing a brick on a Brixi, we decide
     * between the x and y component based on the higher absolute value.
     */
    open fun modifyOffset(toBePlaced: ElementType, offset: Vec2d) = Vec2d(0.0, offset.y)

    /**
     * Calculate an additional rotation for the [toBePlaced]. This is used e.g. to rotate the brixi with the mouse
     * wheel in the placing state. We are the target element here, and toBePlaced is the element that
     * is placed on top of us.
     *
     * The [RotationAround] is in local coordinates relative to our element.
     *
     * @param scrollRotation The rotation from the mouse wheel.
     *
     * @see [EditorManager.currentScrollRotation]
     */
    open fun getRotationAround(
        toBePlaced: ElementType,
        scrollRotation: Int,
        directionForPlaced: Direction,
        offset: Vec3d,
    ) = when (toBePlaced) {
        // todo: fix wrong rotation when brixi is rotated due to offset ...
        Brixi -> {
            val lineOrigin =
                Vec3d(0.0, -scrollRotation.sign * 9.0, -toBePlaced.heightFor(Direction.FORWARD) / 2) + offset
            val lineDirection = Vec3d(x = 1.0)
            val line = Line3d(lineOrigin, lineDirection)

            val angle = ((scrollRotation * PI) / 32).coerceIn(-PI / 2, PI / 2)
            angle around line
        }

        else -> 0.0.aroundZ()
    }
}

/**
 * The element types sorted into categories for the ui.
 */
val elementClasses: Map<String, Set<ElementType>> = mapOf(
    ElementType.Brixi.name to setOf(ElementType.Brixi),
    ElementType.Twister.name to setOf(ElementType.Twister),
    "Brick No. 1" to setOf(
        ElementType.`1x2`, ElementType.`1x3`, ElementType.`1x4`, ElementType.`1x5`
    ),
    "Brick No. 2" to setOf(
        ElementType.`2x2`, ElementType.`2x3`, ElementType.`2x4`, ElementType.`2x5`
    ),
    "Kapla®" to setOf(ElementType.Kapla),
    "Brick No. 4" to setOf(ElementType.`4x3`),
    "Wheels" to setOf(ElementType.Wheels),
)